import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import * as yup from 'yup';

import { datasourceEndpoints } from '@/api/endpoints';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import FileUpload from '@/components/ui/custom/FileUpload/FileUpload';
import useDatasources from '@/hooks/useDatasources';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { ACCEPTED_DATASOURCE_FORMATS } from '@/modules/collections/utils/util';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectDocumentId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { DatasourceType } from '@/shared/types/collections';
import { FIFTEEN_MB } from '@/util/constants';
import { extractNames, generateUniqueName } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { CreateFolderCard } from './CreateFolderCard';
import { ROOT_DATASOURCE_NAMES } from '../../constants';
import { useAPIDatasourcesDocuments } from '../../hooks/useAPIDatasourcesDocuments';
import { useAPIDocuments } from '../../hooks/useAPIDocuments';
import { useTabCategory } from '../../hooks/useCategory';
import { useUpload } from '../../hooks/useUpload';

export const defaultTitle = 'Untitled_folder';

export const UploadDocuments = () => {
  const { collectionId, datasourceId: datasourceIdFromParams } = useParams();
  const documentId = useSelector(selectDocumentId);
  const { t } = useTranslation();
  const { datasources, createDatasourceAsync } = useDatasources(collectionId);
  const { category } = useTabCategory();
  const { getDocumentById } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceIdFromParams,
    document_id: documentId,
  });

  const { getDatasourceDocumentById } = useAPIDatasourcesDocuments({
    collection_id: collectionId,
    datasourceTab: category,
  });
  const rootDatasource = datasources?.find(
    (ds) => ds.type === DatasourceType.FILES && ds.name === ROOT_DATASOURCE_NAMES.documents
  );

  const documentFromInsideFolder = getDocumentById(documentId);
  const documentFromOutsideFolder = getDatasourceDocumentById(documentId);
  const document = documentFromInsideFolder || documentFromOutsideFolder;

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedFiles, setHasUnsavedFiles] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const dispatch = useDispatch();
  const isReplacing = !!documentId;
  const isInsideFolder = !!datasourceIdFromParams;

  const newFolderDatasourceIdRef = useRef<string | null>(null);

  // Create schema with dynamic name validation
  const formSchema = yup.object({
    title: createNameFieldSchema({
      getEndpoint: (name) =>
        datasourceEndpoints.checkName(collectionId, name, { collection_id: collectionId }),
      max: LENGTH_XS,
    }),
  });

  type FormValues = yup.InferType<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(formSchema),
    mode: 'onChange',
    defaultValues: { title: defaultTitle },
  });
  const {
    handleUploadDocumentToRootDatasource,
    handleUploadDocumentToFolderDatasource,
    handleReplaceDocument,
  } = useUpload({ collectionId });

  const resetReduxState = () => {
    dispatch(setDocumentId(null));
    dispatch(setDatasourceId(null));
  };

  const handleClose = () => {
    if (hasUnsavedFiles) {
      setShowUnsavedModal(true);
    } else {
      dispatch(setSidebarContent(null));
      resetReduxState();
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    setHasUnsavedFiles(false);
    setShowNewFolder(false);
    form.reset({ title: defaultTitle });
    dispatch(setSidebarContent(null));
    resetReduxState();
  };

  const handleGoBack = () => {
    setShowUnsavedModal(false);
    resetReduxState();
  };

  const handleUploadClick = async () => {
    if (showNewFolder && !isInsideFolder) {
      const uniqueName = generateUniqueName(
        form.getValues('title') || defaultTitle,
        extractNames(datasources ?? [])
      );
      const datasource = await createDatasourceAsync({
        type: DatasourceType.FILES,
        name: uniqueName,
        config: {},
      });
      newFolderDatasourceIdRef.current = datasource.datasource_id;
    }
  };

  const handleFileUpload = async (file: File) => {
    if (isReplacing) {
      await handleReplaceDocument({ file, document });
      dispatch(setSidebarContent('datasource_details'));
    } else {
      let targetDatasourceId: string;

      if (newFolderDatasourceIdRef.current) {
        targetDatasourceId = newFolderDatasourceIdRef.current;
        await handleUploadDocumentToFolderDatasource(file, targetDatasourceId);
      } else if (isInsideFolder) {
        await handleUploadDocumentToFolderDatasource(file, datasourceIdFromParams);
      } else {
        await handleUploadDocumentToRootDatasource(file, rootDatasource?.datasource_id);
      }
    }

    setHasUnsavedFiles(false);
    setShowNewFolder(false);
    form.reset({ title: defaultTitle });
  };

  const handleFilesChange = (files: File[]) => {
    // Simply track if there are files to upload
    // Server-side validation will handle duplicate name checking
    setHasUnsavedFiles(files.length > 0);
  };

  useTrackFormState({ isDirty: hasUnsavedFiles, formId: 'upload-documents' });
  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="truncate text-base leading-5 font-medium">
          {isReplacing
            ? `${t('collections.documents.replace_document')} ${document?.name}`
            : t('collections.documents.upload_documents')}
        </h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <div className="space-y-4 p-6">
        <FileUpload
          maxSizeMB={FIFTEEN_MB / 1000000}
          acceptedFormats={ACCEPTED_DATASOURCE_FORMATS}
          onUpload={handleFileUpload}
          onFilesChange={handleFilesChange}
          maxFiles={isReplacing ? 1 : undefined}
          onUploadClick={handleUploadClick}
        >
          {hasUnsavedFiles && !isInsideFolder && !isReplacing && (
            <CreateFolderCard
              form={form}
              enabled={showNewFolder}
              onEnabledChange={setShowNewFolder}
            />
          )}
        </FileUpload>
      </div>

      {showUnsavedModal && (
        <UnsavedChangesModal
          onOpenChange={setShowUnsavedModal}
          onDiscard={handleDiscardChanges}
          onGoBack={handleGoBack}
        />
      )}
    </SidebarWrapper>
  );
};
